import type { ComponentProps } from 'react'
import type { LinkProps } from '@remix-run/react'
import { Link } from '@remix-run/react'
import { HomeIcon, ChevronRightIcon } from '@heroicons/react/20/solid'

import { classNames } from '~/utils/client/helpers'

/**
 * Example usage:
 *
 *      <Breadcrumbs>
 *       <BreadcrumbHomeItem to={route('/kundenzugang')} />
 *       <BreadcrumbLinkItem to={route('/kundenzugang/depot/:id', { id })}>Depot</BreadcrumbLinkItem>
 *     </Breadcrumbs>
 */

export const BreadcrumbHomeItem = ({ className, ...linkProps }: LinkProps) => {
  return (
    <li>
      <BreadcrumbItem {...linkProps} className={className}>
        <HomeIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        <span className="sr-only">Home</span>
      </BreadcrumbItem>
    </li>
  )
}

export const BreadcrumbItem = ({ children, className, ...linkProps }: LinkProps) => {
  return (
    <Link
      {...linkProps}
      className={classNames('text-muted hover:text-secondary flex items-center', className)}
    >
      {children}
    </Link>
  )
}

export const BreadcrumbLinkItem = ({ children, className, ...linkProps }: LinkProps) => {
  return (
    <li className="flex items-center">
      <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-muted" aria-hidden="true" />
      <BreadcrumbItem
        {...linkProps}
        className={classNames(
          'text-sm font-medium text-secondary hover:text-body capitalize',
          className
        )}
      >
        {children}
      </BreadcrumbItem>
    </li>
  )
}

export const Breadcrumbs = ({
  children,
  hasPadding = true,
  className,
}: ComponentProps<'nav'> & { hasPadding?: boolean }) => {
  const dynamicClassName = classNames(hasPadding && 'pb-8', 'hidden sm:flex', className)

  return (
    <nav className={dynamicClassName} aria-label="Breadcrumb">
      <ol className="flex items-center">{children}</ol>
    </nav>
  )
}
